import { useEffect, useState } from "react";
import { authFetch } from "../../lib/auth-fetch";
import { toProxiedMediaUrl } from "../../lib/media-proxy";

export interface Fixture {
	fixtureId: number;
	date: string;
	status: string;
	league: string;
	homeTeam: string;
	awayTeam: string;
	homeLogo: string;
	awayLogo: string;
	venue: string;
	location: string;
}

interface FixturePickerProps {
	onSelect: (fixture: Fixture) => void;
	onManual: () => void;
}

type LoadState = "loading" | "error" | "ready";

const formatKickoff = (date: string) => {
	const d = new Date(date);
	return d.toLocaleString(undefined, {
		weekday: "short",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const FixturePicker = ({ onSelect, onManual }: FixturePickerProps) => {
	const [fixtures, setFixtures] = useState<Fixture[]>([]);
	const [state, setState] = useState<LoadState>("loading");
	const [useMock, setUseMock] = useState(true);
	const [leagueId, setLeagueId] = useState("1");
	const [submittedLeagueId, setSubmittedLeagueId] = useState("1");

	const [errorMsg, setErrorMsg] = useState<string | null>(null);

	useEffect(() => {
		setState("loading");
		setErrorMsg(null);
		const path = useMock
			? "/api/fixtures?mock=true"
			: `/api/fixtures?mock=false&league=${submittedLeagueId}`;
		authFetch(path)
			.then(async (res) => {
				if (!res.ok) {
					const body = (await res.json().catch(() => ({}))) as {
						error?: string;
					};
					throw new Error(body.error ?? "Bad response from server");
				}
				return res.json() as Promise<Fixture[]>;
			})
			.then((data) => {
				setFixtures(data);
				setState("ready");
			})
			.catch((err: Error) => {
				setErrorMsg(err.message);
				setState("error");
			});
	}, [useMock, submittedLeagueId]);

	return (
		<div>
			<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
				<label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
					<input
						type="checkbox"
						checked={!useMock}
						onChange={(e) => setUseMock(!e.target.checked)}
					/>
					Use live fixtures <small>(costs API credits)</small>
				</label>
				{!useMock && (
					<span
						style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
					>
						<small>League ID:</small>
						<input
							type="number"
							value={leagueId}
							onChange={(e) => setLeagueId(e.target.value)}
							style={{ width: "5rem" }}
						/>
						<button
							type="button"
							onClick={() => setSubmittedLeagueId(leagueId)}
							disabled={leagueId === submittedLeagueId}
						>
							Go
						</button>
					</span>
				)}
			</div>

			{state === "loading" && <p>Loading fixtures…</p>}

			{state === "error" && (
				<div>
					<p>Could not load fixtures{errorMsg ? `: ${errorMsg}` : ""}.</p>
					<button type="button" onClick={onManual}>
						Enter manually
					</button>
				</div>
			)}

			{state === "ready" && (
				<div>
					{fixtures.length === 0 ? (
						<p>No upcoming fixtures found.</p>
					) : (
						fixtures.map((fixture) => (
							<div key={fixture.fixtureId}>
								<button type="button" onClick={() => onSelect(fixture)}>
									<span
										style={{
											display: "inline-flex",
											alignItems: "center",
											gap: "0.4rem",
										}}
									>
										{fixture.homeLogo && (
											<img
												src={toProxiedMediaUrl(fixture.homeLogo)}
												alt=""
												width={20}
												height={20}
											/>
										)}
										{fixture.homeTeam} vs {fixture.awayTeam}
										{fixture.awayLogo && (
											<img
												src={toProxiedMediaUrl(fixture.awayLogo)}
												alt=""
												width={20}
												height={20}
											/>
										)}
									</span>
									<span> — {fixture.league}</span>
									<span> — {formatKickoff(fixture.date)}</span>
								</button>
							</div>
						))
					)}
					<button type="button" onClick={onManual}>
						Enter manually instead
					</button>
				</div>
			)}
		</div>
	);
};

export default FixturePicker;
